import { useEffect, useState } from 'react';
import { requestReportAssetContent } from '../../../api/requests/reportTemplates/requestReportTemplates';
import { useUserState } from '../../../hooks/context/useUserState';
import { ReportBlock, ReportTemplate } from '../types';

const collectFromBlocks = (blocks: ReportBlock[], ids: Set<string>): void => {
  blocks.forEach((block) => {
    if (block.kind === 'logo' && block.assetId) ids.add(block.assetId);
    if (block.kind === 'row') {
      block.cells.forEach((cell) => collectFromBlocks(cell.blocks, ids));
    }
  });
};

export const collectReportAssetIds = (template: ReportTemplate): string[] => {
  const ids = new Set<string>();
  collectFromBlocks(template.sections.header?.blocks ?? [], ids);
  collectFromBlocks(template.sections.footer?.blocks ?? [], ids);
  return Array.from(ids);
};

export const reportAssetIdsKey = (
  template: ReportTemplate | undefined
): string => JSON.stringify(template ? collectReportAssetIds(template) : []);

export interface ReportAssetUrlState {
  urls: Record<string, string>;
  isLoading: boolean;
  failed: boolean;
}

const IDLE: ReportAssetUrlState = { urls: {}, isLoading: false, failed: false };

export const createDecodedImageUrl = async (blob: Blob): Promise<string> => {
  const url = URL.createObjectURL(blob);

  try {
    await new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () =>
        reject(new Error('Report asset image decoding failed'));
      image.src = url;
    });
    return url;
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

/**
 * Resolves a referenced asset set atomically. The first failure escapes
 * immediately; existing URLs are revoked, and slower successes revoke
 * themselves when they observe that shared failure.
 */
export const loadDecodedReportAssetUrls = async (
  assetIds: readonly string[],
  loadBlob: (assetId: string) => Promise<Blob>,
  decode: (blob: Blob) => Promise<string> = createDecodedImageUrl,
  revoke: (url: string) => void = (url) => URL.revokeObjectURL(url),
  onDecoded: (url: string) => void = () => undefined
): Promise<readonly (readonly [string, string])[]> => {
  let hasFailed = false;
  let firstFailure: unknown;
  const createdUrls = new Set<string>();

  return Promise.all(
    assetIds.map(async (assetId) => {
      try {
        const url = await decode(await loadBlob(assetId));
        onDecoded(url);
        if (hasFailed) {
          revoke(url);
          throw firstFailure;
        }
        createdUrls.add(url);
        return [assetId, url] as const;
      } catch (error) {
        if (!hasFailed) {
          hasFailed = true;
          firstFailure = error;
          createdUrls.forEach(revoke);
          createdUrls.clear();
        }
        throw firstFailure;
      }
    })
  );
};

export const useReportAssetUrls = (
  template: ReportTemplate | undefined
): ReportAssetUrlState => {
  const {
    state: { token },
  } = useUserState();
  // DatasetContext may recreate an equivalent template object on any render.
  // Key the effect on the referenced UUIDs, not that object identity, or its
  // own setState would restart asset loading and page measurement forever.
  const assetIdsKey = reportAssetIdsKey(template);
  const [state, setState] = useState<ReportAssetUrlState>(IDLE);

  useEffect(() => {
    const assetIds = JSON.parse(assetIdsKey) as string[];
    if (assetIds.length === 0) {
      setState(IDLE);
      return;
    }

    let cancelled = false;
    const createdUrls = new Set<string>();
    const revokeCreatedUrl = (url: string): void => {
      URL.revokeObjectURL(url);
      createdUrls.delete(url);
    };
    setState({ urls: {}, isLoading: true, failed: false });
    loadDecodedReportAssetUrls(
      assetIds,
      (assetId) => requestReportAssetContent(assetId, token),
      createDecodedImageUrl,
      revokeCreatedUrl,
      (url) => {
        if (cancelled) revokeCreatedUrl(url);
        else createdUrls.add(url);
      }
    )
      .then((entries) => {
        if (cancelled) {
          entries.forEach(([, url]) => revokeCreatedUrl(url));
          return;
        }
        setState({
          urls: Object.fromEntries(entries),
          isLoading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ urls: {}, isLoading: false, failed: true });
      });

    return () => {
      cancelled = true;
      Array.from(createdUrls).forEach(revokeCreatedUrl);
    };
  }, [assetIdsKey, token]);

  return state;
};
